'use client';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { INITIAL_DOTS, getDotColor, formatTimestamp, getCameraLabel } from '@/data/dotsData';
import type { DotPerson, DotColor } from '@/data/dotsData';
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  SignalSlashIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronUpDownIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

type EventType = 'disconnection' | 'reconnection' | 'offline';

interface AuditEntry {
  id: string;
  eventType: EventType;
  personnelId: string;
  personnelName: string;
  cameras: string;
  eventTimestamp: string;
  confirmed: boolean;
  confirmedBy?: string;
  confirmedAt?: string;
  notes?: string;
  dotColor: DotColor;
}

const OPERATORS = [
  'Operador Principal',
  'Operador Turno A',
  'Operador Turno B',
  'Supervisor Nocturno',
  'Sistema',
];

const NOTES_POOL = [
  'Revisión manual completada, equipo sin daños.',
  'Se notificó al personal de campo.',
  'Falla de señal por interferencia en zona.',
  'Reconexión automática tras reinicio de router.',
  'Pendiente revisión técnica en sitio.',
  'Batería baja detectada en dispositivo.',
  'Corte de energía en sector reportado.',
  undefined,
  undefined,
  undefined,
];

function generateAuditEntries(dots: DotPerson[]): AuditEntry[] {
  const entries: AuditEntry[] = [];
  let seed = 42;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(seed) / 0x7fffffff;
  };

  dots.forEach((dot, i) => {
    const color = getDotColor(dot.lastUpdate);
    const diffMs = Date.now() - new Date(dot.lastUpdate).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const opIdx = Math.floor(rand() * OPERATORS.length);
    const noteIdx = Math.floor(rand() * NOTES_POOL.length);

    if (color === 'red') {
      // Offline event
      entries.push({
        id: `audit-offline-${dot.id}`,
        eventType: 'offline',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        eventTimestamp: dot.lastUpdate,
        confirmed: diffMin > 70,
        confirmedBy: diffMin > 70 ? OPERATORS[opIdx] : undefined,
        confirmedAt: diffMin > 70
          ? new Date(new Date(dot.lastUpdate).getTime() + Math.floor(rand() * 15 + 5) * 60000).toISOString()
          : undefined,
        notes: diffMin > 70 ? NOTES_POOL[noteIdx] : undefined,
        dotColor: 'red',
      });
      // Disconnection event ~30 min before
      const discTime = new Date(new Date(dot.lastUpdate).getTime() - 30 * 60000).toISOString();
      const op2 = OPERATORS[Math.floor(rand() * OPERATORS.length)];
      const note2 = NOTES_POOL[Math.floor(rand() * NOTES_POOL.length)];
      entries.push({
        id: `audit-disc-${dot.id}`,
        eventType: 'disconnection',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        eventTimestamp: discTime,
        confirmed: diffMin > 90,
        confirmedBy: diffMin > 90 ? op2 : undefined,
        confirmedAt: diffMin > 90
          ? new Date(new Date(discTime).getTime() + Math.floor(rand() * 12 + 3) * 60000).toISOString()
          : undefined,
        notes: diffMin > 90 ? note2 : undefined,
        dotColor: 'red',
      });
    } else if (color === 'yellow') {
      const op = OPERATORS[Math.floor(rand() * OPERATORS.length)];
      const note = NOTES_POOL[Math.floor(rand() * NOTES_POOL.length)];
      entries.push({
        id: `audit-disc-${dot.id}`,
        eventType: 'disconnection',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        eventTimestamp: dot.lastUpdate,
        confirmed: rand() > 0.5,
        confirmedBy: rand() > 0.5 ? op : undefined,
        confirmedAt: rand() > 0.5
          ? new Date(new Date(dot.lastUpdate).getTime() + Math.floor(rand() * 10 + 2) * 60000).toISOString()
          : undefined,
        notes: rand() > 0.6 ? note : undefined,
        dotColor: 'yellow',
      });
    } else {
      if (diffMin > 2) {
        entries.push({
          id: `audit-reconn-${dot.id}`,
          eventType: 'reconnection',
          personnelId: dot.dotId,
          personnelName: dot.name,
          cameras: getCameraLabel(dot.cameras),
          eventTimestamp: dot.lastUpdate,
          confirmed: true,
          confirmedBy: 'Sistema',
          confirmedAt: dot.lastUpdate,
          notes: undefined,
          dotColor: 'blue',
        });
      }
    }
  });

  return entries.sort((a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime());
}

const EVENT_META: Record<EventType, { label: string; color: string; bg: string; border: string; icon: React.ElementType }> = {
  disconnection: { label: 'Desconexión', color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30', icon: SignalSlashIcon },
  offline: { label: 'Fuera de línea', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30', icon: ExclamationTriangleIcon },
  reconnection: { label: 'Reconexión', color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30', icon: SignalIcon },
};

type SortField = 'eventTimestamp' | 'confirmedAt' | 'personnelName' | 'eventType';
type SortDir = 'asc' | 'desc';

export default function AuditLogScreen() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [lastRefresh, setLastRefresh] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOperator, setFilterOperator] = useState('all');
  const [filterEventType, setFilterEventType] = useState<'all' | EventType>('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterConfirmed, setFilterConfirmed] = useState<'all' | 'confirmed' | 'pending'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Sort
  const [sortField, setSortField] = useState<SortField>('eventTimestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  
  useEffect(() => {
    setEntries(generateAuditEntries(INITIAL_DOTS));
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setLastRefresh(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
  }, []);

  function handleRefresh() {
    setEntries(generateAuditEntries(INITIAL_DOTS));
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setLastRefresh(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
  }

  function handleConfirm(id: string) {
    setEntries(prev =>
      prev.map(e =>
        e.id === id
          ? { ...e, confirmed: true, confirmedBy: 'Operador Principal', confirmedAt: new Date().toISOString(), notes: e.notes ?? 'Confirmado manualmente.' }
          : e
      )
    );
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }

  const allOperators = useMemo(() => {
    const ops = new Set<string>();
    entries.forEach(e => { if (e.confirmedBy) ops.add(e.confirmedBy); });
    return ['all', ...Array.from(ops)];
  }, [entries]);

  const filtered = useMemo(() => {
    let result = entries.filter(e => {
      if (filterEventType !== 'all' && e.eventType !== filterEventType) return false;
      if (filterOperator !== 'all' && e.confirmedBy !== filterOperator) return false;
      if (filterConfirmed === 'confirmed' && !e.confirmed) return false;
      if (filterConfirmed === 'pending' && e.confirmed) return false;
      if (filterDateFrom) {
        const from = new Date(filterDateFrom).getTime();
        if (new Date(e.eventTimestamp).getTime() < from) return false;
      }
      if (filterDateTo) {
        const to = new Date(filterDateTo).getTime() + 86400000;
        if (new Date(e.eventTimestamp).getTime() > to) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !e.personnelName.toLowerCase().includes(q) &&
          !e.personnelId.toLowerCase().includes(q) &&
          !(e.confirmedBy ?? '').toLowerCase().includes(q) &&
          !(e.notes ?? '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });

    result = [...result].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      if (sortField === 'eventTimestamp') { va = new Date(a.eventTimestamp).getTime(); vb = new Date(b.eventTimestamp).getTime(); }
      else if (sortField === 'confirmedAt') { va = a.confirmedAt ? new Date(a.confirmedAt).getTime() : 0; vb = b.confirmedAt ? new Date(b.confirmedAt).getTime() : 0; }
      else if (sortField === 'personnelName') { va = a.personnelName; vb = b.personnelName; }
      else if (sortField === 'eventType') { va = a.eventType; vb = b.eventType; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [entries, filterEventType, filterOperator, filterConfirmed, filterDateFrom, filterDateTo, searchQuery, sortField, sortDir]);

  const totalConfirmed = entries.filter(e => e.confirmed).length;
  const totalPending = entries.filter(e => !e.confirmed).length;
  const totalDisconnections = entries.filter(e => e.eventType === 'disconnection').length;
  const totalOffline = entries.filter(e => e.eventType === 'offline').length;

  const activeFilters = [
    filterEventType !== 'all',
    filterOperator !== 'all',
    filterConfirmed !== 'all',
    !!filterDateFrom,
    !!filterDateTo,
  ].filter(Boolean).length;

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ChevronUpDownIcon className="w-3.5 h-3.5 text-muted-foreground/40" />;
    return (
      <span className={`text-primary text-xs font-bold`}>{sortDir === 'asc' ? '↑' : '↓'}</span>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            title="Volver al mapa"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClipboardDocumentListIcon className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">Bitácora de Auditoría</h1>
            <p className="text-xs text-muted-foreground">Registro detallado de confirmaciones de eventos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground hidden sm:block">Actualizado: {lastRefresh}</span>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Actualizar
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            title="Exportar CSV"
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            Exportar
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-px bg-border shrink-0">
        {[
          { label: 'Total eventos', value: entries.length, color: 'text-foreground', bg: 'bg-background' },
          { label: 'Confirmados', value: totalConfirmed, color: 'text-emerald-400', bg: 'bg-background' },
          { label: 'Pendientes', value: totalPending, color: 'text-amber-400', bg: 'bg-amber-400/5' },
          { label: 'Fuera de línea', value: totalOffline, color: 'text-red-400', bg: 'bg-background' },
        ].map(kpi => (
          <div key={kpi.label} className={`flex flex-col items-center justify-center py-3 ${kpi.bg}`}>
            <span className={`text-xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</span>
            <span className="text-xs text-muted-foreground mt-0.5">{kpi.label}</span>
          </div>
        ))}
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-border shrink-0 flex-wrap">
        {/* Search */}
        <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Buscar personal, operador, notas…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none w-full"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-muted-foreground hover:text-foreground">
              <XMarkIcon className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(p => !p)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-150 ${
            showFilters || activeFilters > 0
              ? 'border-primary text-primary bg-primary/5' :'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <FunnelIcon className="w-3.5 h-3.5" />
          Filtros
          {activeFilters > 0 && (
            <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
              {activeFilters}
            </span>
          )}
        </button>

        {/* Quick status pills */}
        <div className="flex gap-1 ml-auto">
          {(['all', 'confirmed', 'pending'] as const).map(v => (
            <button
              key={v}
              onClick={() => setFilterConfirmed(v)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                filterConfirmed === v
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {v === 'all' ? 'Todos' : v === 'confirmed' ? 'Confirmados' : 'Pendientes'}
            </button>
          ))}
        </div>
      </div>

      {/* Expanded filter panel */}
      {showFilters && (
        <div className="px-6 py-3 border-b border-border bg-muted/30 shrink-0 flex flex-wrap gap-4 items-end">
          {/* Event type */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tipo de evento</label>
            <select
              value={filterEventType}
              onChange={e => setFilterEventType(e.target.value as 'all' | EventType)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none min-w-[140px]"
            >
              <option value="all">Todos los tipos</option>
              <option value="disconnection">Desconexión</option>
              <option value="offline">Fuera de línea</option>
              <option value="reconnection">Reconexión</option>
            </select>
          </div>

          {/* Operator */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Operador</label>
            <select
              value={filterOperator}
              onChange={e => setFilterOperator(e.target.value)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none min-w-[160px]"
            >
              <option value="all">Todos los operadores</option>
              {allOperators.filter(o => o !== 'all').map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Desde</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={e => setFilterDateFrom(e.target.value)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none"
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Hasta</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={e => setFilterDateTo(e.target.value)}
              className="bg-card border border-border rounded-lg px-2 py-1.5 text-xs text-foreground outline-none"
            />
          </div>

          {/* Clear */}
          {activeFilters > 0 && (
            <button
              onClick={() => {
                setFilterEventType('all');
                setFilterOperator('all');
                setFilterDateFrom('');
                setFilterDateTo('');
                setFilterConfirmed('all');
              }}
              className="px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      <div className="px-6 py-2 border-b border-border shrink-0 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Mostrando <span className="font-medium text-foreground">{filtered.length}</span> de {entries.length} registros
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead className="sticky top-0 z-10 bg-card border-b border-border">
            <tr>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('eventType')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Tipo <SortIcon field="eventType" />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('personnelName')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Personal <SortIcon field="personnelName" />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Cámaras</span>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('eventTimestamp')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Fecha evento <SortIcon field="eventTimestamp" />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Estado</span>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('confirmedAt')}
                  className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Confirmado por <SortIcon field="confirmedAt" />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Notas</span>
              </th>
              <th className="text-left px-4 py-3">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Acción</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16 text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <ClipboardDocumentListIcon className="w-10 h-10 opacity-20" />
                    <p className="text-sm">No hay registros para los filtros seleccionados</p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((entry, idx) => {
                const meta = EVENT_META[entry.eventType];
                const EventIcon = meta.icon;
                return (
                  <tr
                    key={entry.id}
                    className={`border-b border-border transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? 'bg-background' : 'bg-card/30'}`}
                  >
                    {/* Event type */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.bg} ${meta.color} border ${meta.border}`}>
                        <EventIcon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>

                    {/* Personnel */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${entry.dotColor === 'blue' ? 'bg-blue-500' : entry.dotColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <div>
                          <p className="text-xs font-medium text-foreground">{entry.personnelName}</p>
                          <p className="text-[11px] text-muted-foreground">#{entry.personnelId}</p>
                        </div>
                      </div>
                    </td>

                    {/* Cameras */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground">{entry.cameras}</span>
                    </td>

                    {/* Event timestamp */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <ClockIcon className="w-3 h-3 shrink-0" />
                        <span>{formatTimestamp(entry.eventTimestamp)}</span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {entry.confirmed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-400/10 text-emerald-400 border border-emerald-400/30">
                          <CheckCircleSolid className="w-3 h-3" />
                          Confirmado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-400/10 text-amber-400 border border-amber-400/30">
                          Pendiente
                        </span>
                      )}
                    </td>

                    {/* Confirmed by */}
                    <td className="px-4 py-3">
                      {entry.confirmed && entry.confirmedBy ? (
                        <div>
                          <div className="flex items-center gap-1.5 text-xs text-foreground">
                            <UserIcon className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="font-medium">{entry.confirmedBy}</span>
                          </div>
                          {entry.confirmedAt && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 pl-4">{formatTimestamp(entry.confirmedAt)}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </td>

                    {/* Notes */}
                    <td className="px-4 py-3 max-w-[200px]">
                      {entry.notes ? (
                        <p className="text-xs text-muted-foreground line-clamp-2" title={entry.notes}>{entry.notes}</p>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      {!entry.confirmed ? (
                        <button
                          onClick={() => handleConfirm(entry.id)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium hover:bg-primary/20 transition-all duration-150 whitespace-nowrap"
                        >
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Confirmar
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
